import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  VLKPageHeader, VLKDataTable, VLKStatusBadge,
} from "@/components/vlk";

/** Small unmistakable Demo v2 badge shown beside Demo institution
 *  names so an operator scanning the list can never mistake a Demo
 *  row for a production tenant. Demo institutions are identified by
 *  the reserved `DEMO-` id prefix or the `_demo` marker on the row. */
function DemoTag({ row }) {
  const isDemo =
    (typeof row?.vlk_inst_id === "string" && row.vlk_inst_id.startsWith("DEMO-"))
    || row?._demo === true
    || row?._showcase === true;
  if (!isDemo) return null;
  return (
    <span
      className="ml-2 inline-flex items-center gap-1 rounded-full bg-orange-500/20 border border-orange-500/40 px-2 py-0.5 text-[9px] font-mono font-bold uppercase tracking-widest text-orange-500"
      data-testid={`institution-row-demo-tag-${row.vlk_inst_id}`}
      title="Demo v2 tenant — isolated from production"
    >
      Demo v2
    </span>
  );
}

export default function Institutions() {
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["institutions"], queryFn: () => api.get("/institutions").then(r => r.data) });

  return (
    <div>
      <VLKPageHeader
        title="Institutions"
        description="Verified organizations authorized to invoke VeraLok."
      />
      <VLKDataTable
        columns={[
          { key: "vlk_inst_id", header: "VLK_INST_ID", mono: true, render: (r) => <span className="font-mono text-xs">{r.vlk_inst_id}</span> },
          { key: "name", header: "Institution", render: (r) => <span className="font-medium">{r.name}<DemoTag row={r} /></span> },
          { key: "type", header: "Type", render: (r) => <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{r.type}</span> },
          { key: "country", header: "Country", mono: true },
          { key: "status", header: "Status", render: (r) => <VLKStatusBadge status={r.status.toLowerCase()}>{r.status}</VLKStatusBadge> },
          { key: "verifications_count", header: "Verifs", mono: true, align: "right", render: (r) => <span className="tabular">{(r.verifications_count || 0).toLocaleString()}</span> },
          { key: "primary_contact_email", header: "Contact", render: (r) => <span className="font-mono text-xs">{r.primary_contact_email}</span> },
        ]}
        rows={data?.items || []}
        rowKey={(r) => r.vlk_inst_id}
        rowTestId={(r) => `institution-row-${r.vlk_inst_id}`}
        onRowClick={(r) => navigate(`/institutions/${r.vlk_inst_id}`)}
        empty="No institutions onboarded yet."
      />
    </div>
  );
}
